import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import { BENEFIT_OPTIONS } from '../../constants/benefitOptions';
import { PHYSICAL_REQUIREMENT_OPTIONS } from '../../constants/physicalRequirementOptions';
import { extractDocumentText } from '../../utils/extractDocumentText';
import LanguageToggle from '../../components/LanguageToggle';
import { resolveEmployerId } from '../../utils/resolveEmployerId';
import { useTrialDays } from '../../utils/trialDays';
import VocationWorksLogo from '../../components/VocationWorksLogo';
import LineIcon from '../../components/LineIcon';

const NAVY      = '#0D1B2A';
const AMBER     = '#F5A623';
const NAVY_MID  = '#132233';
const TEXT      = '#FFFFFF';
const YELLOW    = '#FACC15';
const BORDER    = 'rgba(255,255,255,0.12)';
const INPUT_BG  = 'rgba(255,255,255,0.06)';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

// Same dark tokens as the employer dashboard (EmployerDashboard.jsx inp/sel), at 16px.
const inp = {
  width: '100%', minHeight: '44px', padding: '11px 13px', fontSize: '16px',
  border: `1.5px solid ${BORDER}`, borderRadius: '8px',
  color: TEXT, outline: 'none', backgroundColor: INPUT_BG,
  fontFamily: FONT_BODY, colorScheme: 'dark',
};
const sel = { ...inp, cursor: 'pointer' };
const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: TEXT, marginBottom: '5px' };
const fld = { marginBottom: '1.25rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' };

const TRADE_OPTIONS = [
  { value: 'Electrician',                     key: 'electrician' },
  { value: 'Plumber',                         key: 'plumber' },
  { value: 'Welder and Fabricator',           key: 'welderFabricator' },
  { value: 'HVAC Technician',                 key: 'hvacTechnician' },
  { value: 'Carpenter',                       key: 'carpenter' },
  { value: 'Machine Operator',                key: 'machineOperator' },
  { value: 'Crane Operator',                  key: 'craneOperator' },
  { value: 'Forklift Operator',               key: 'forkliftOperator' },
  { value: 'Industrial Maintenance',          key: 'industrialMaintenance' },
  { value: 'CNC Operator',                    key: 'cncOperator' },
  { value: 'Diesel Mechanic',                 key: 'dieselMechanic' },
  { value: 'Pipefitter',                      key: 'pipefitter' },
  { value: 'Sheet Metal Worker',              key: 'sheetMetalWorker' },
  { value: 'Structural Iron and Steel Worker', key: 'structuralIronSteelWorker' },
  { value: 'CDL/Truck Driver',                key: 'cdlTruckDriver' },
  { value: 'Other Skilled Trade',             key: 'otherSkilledTrade' },
];

const SHIFT_OPTIONS = [
  { value: '1st shift', key: 'first' },
  { value: '2nd shift', key: 'second' },
  { value: '3rd shift', key: 'third' },
  { value: 'Rotating',  key: 'rotating' },
  { value: 'Flexible',  key: 'flexible' },
];

const BENEFIT_KEY_MAP = {
  'Health insurance':      'healthInsurance',
  'Dental insurance':      'dentalInsurance',
  'Vision insurance':      'visionInsurance',
  '401(k)':                'retirement401k',
  'Paid time off':         'paidTimeOff',
  'Life insurance':        'lifeInsurance',
  'Tuition reimbursement': 'tuitionReimbursement',
  'Tool allowance':        'toolAllowance',
  'Overtime available':    'overtimeAvailable',
};

const PHYSICAL_REQUIREMENT_KEY_MAP = {
  'Heavy lifting (50+ lbs)':                  'heavyLifting',
  'Working at heights':                       'workingAtHeights',
  'Outdoor work in all weather':              'outdoorWork',
  'Standing for long periods':                'standingLongPeriods',
  'Confined spaces':                          'confinedSpaces',
  'Frequent bending, kneeling, or crouching': 'bendingKneeling',
  'Exposure to loud noise':                   'loudNoise',
};

function ToggleGroup({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
      {options.map(opt => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            style={{
              minHeight: '44px', padding: '9px 20px', borderRadius: '8px',
              border: `2px solid ${active ? AMBER : BORDER}`,
              backgroundColor: active ? AMBER : INPUT_BG,
              color: active ? NAVY : 'rgba(255,255,255,0.6)',
              fontWeight: 600, fontSize: '0.875rem',
              cursor: 'pointer', fontFamily: FONT_BODY,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export default function PostJob() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const trialDays = useTrialDays();
  const JOB_TYPE_OPTIONS = [
    { value: 'Full-time',           label: t('employer.postJob.jobTypeOptions.fullTime') },
    { value: 'Part-time',           label: t('employer.postJob.jobTypeOptions.partTime') },
    { value: 'Contract/Freelance',  label: t('employer.postJob.jobTypeOptions.contractFreelance') },
    { value: 'Temporary',           label: t('employer.postJob.jobTypeOptions.temporary') },
    { value: 'Seasonal',            label: t('employer.postJob.jobTypeOptions.seasonal') },
  ];
  const UNION_STATUS_OPTIONS = [
    { value: 'Union',         label: t('employer.postJob.unionStatusOptions.union') },
    { value: 'Non-union',     label: t('employer.postJob.unionStatusOptions.nonUnion') },
    { value: 'Open to both',  label: t('employer.postJob.unionStatusOptions.openToBoth') },
  ];
  const duplicateJob = location.state?.duplicateJob || null;

  const [jobTitle, setJobTitle]                     = useState(duplicateJob?.job_title || '');
  const [multipleOpenings, setMultipleOpenings]     = useState((duplicateJob?.number_of_openings || 1) > 1);
  const [numberOfOpenings, setNumberOfOpenings]     = useState((duplicateJob?.number_of_openings || 1) > 1 ? String(duplicateJob.number_of_openings) : '');
  const [tradeCategory, setTradeCategory]           = useState(duplicateJob?.trade_category || '');
  const [jobType, setJobType]                       = useState(duplicateJob?.job_type || 'Full-time');
  const [unionStatus, setUnionStatus]               = useState(duplicateJob?.union_status || 'Open to both');
  const [salaryMin, setSalaryMin]                   = useState(duplicateJob?.salary_min ?? '');
  const [salaryMax, setSalaryMax]                   = useState(duplicateJob?.salary_max ?? '');
  const [minYearsExperience, setMinYearsExperience] = useState(duplicateJob?.min_years_experience || '');
  const [certTags, setCertTags]   = useState(Array.isArray(duplicateJob?.certifications_required) ? duplicateJob.certifications_required : []);
  const [requireCdl, setRequireCdl] = useState(Array.isArray(duplicateJob?.licenses_required) && duplicateJob.licenses_required.includes('CDL'));
  const [certInput, setCertInput] = useState('');
  const [certPreferredTags, setCertPreferredTags]   = useState(Array.isArray(duplicateJob?.certifications_preferred) ? duplicateJob.certifications_preferred : []);
  const [certPreferredInput, setCertPreferredInput] = useState('');
  const [jobDescription, setJobDescription]         = useState(duplicateJob?.job_description || '');
  const [descExtracting, setDescExtracting]         = useState(false);
  const [descExtractError, setDescExtractError]     = useState('');
  const descFileInputRef                            = useRef(null);
  const [city, setCity]                             = useState(duplicateJob?.city || '');
  const [state, setState]                           = useState(duplicateJob?.state || '');
  const [zip, setZip]                               = useState(duplicateJob?.zip || '');
  const [shift, setShift]                           = useState(duplicateJob?.shift || '');
  const [perDiem, setPerDiem]                       = useState(!!duplicateJob?.per_diem);
  const [perDiemAmount, setPerDiemAmount]           = useState(duplicateJob?.per_diem_amount || '');
  const [signOnBonus, setSignOnBonus]               = useState(!!duplicateJob?.sign_on_bonus_amount);
  const [signOnBonusAmount, setSignOnBonusAmount]   = useState(duplicateJob?.sign_on_bonus_amount || '');
  const [travelRequired, setTravelRequired]         = useState(!!duplicateJob?.travel_required);
  const [travelDetails, setTravelDetails]           = useState(duplicateJob?.travel_details || '');
  const [benefits, setBenefits]                     = useState(Array.isArray(duplicateJob?.benefits) ? duplicateJob.benefits : []);
  const [urgentlyHiring, setUrgentlyHiring]         = useState(!!duplicateJob?.urgently_hiring);
  const [willingToTrain, setWillingToTrain]         = useState(!!duplicateJob?.willing_to_train);
  const [featured, setFeatured]                     = useState(!!duplicateJob?.featured);
  const [planTier, setPlanTier]                     = useState('');
  const [physicalRequirements, setPhysicalRequirements] = useState(Array.isArray(duplicateJob?.physical_requirements) ? duplicateJob.physical_requirements : []);
  const [expiresAt, setExpiresAt]                   = useState(duplicateJob?.expires_at || '');

  // Friendly pre-check only (before showing the form, per the task) -- the real block is the
  // jobs INSERT RLS policy (employer_can_add_job), which stays in force regardless of this.
  // null = not loaded yet, so the form shows by default rather than blocking on a slow/failed
  // fetch; the DB is the real backstop either way.
  const [planUsage, setPlanUsage] = useState(null);
  // True once the employer_plan_usage answer is in (or failed). Until then a neutral loading
  // screen shows, so the subscribe gate never flashes before the subscription is known.
  const [usageLoaded, setUsageLoaded] = useState(false);

  useEffect(() => {
    // Only the latest run may decide the gate: a superseded run (e.g. React re-running the
    // effect) must not render a stale answer.
    let cancelled = false;
    const loadEmployerData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/login'); return; }

      const employerId = await resolveEmployerId(supabase, session.user.id);
      if (cancelled || !employerId) return;
      const { data: employer } = await supabase
        .from('employers')
        .select('city, state, typical_benefits, plan_tier')
        .eq('id', employerId)
        .single();

      if (employer && !duplicateJob) {
        setCity(employer.city || '');
        setState(employer.state || '');
        const typicalBenefits = Array.isArray(employer.typical_benefits) ? employer.typical_benefits : [];
        setBenefits(BENEFIT_OPTIONS.filter(b => typicalBenefits.some(t => t.trim().toLowerCase() === b.toLowerCase())));
      }
      if (employer) setPlanTier(employer.plan_tier || '');

      const { data: usage } = await supabase.rpc('employer_plan_usage', { p_employer_id: employerId });
      if (!cancelled && usage) setPlanUsage(usage);
    };
    // Fails open: an error still ends the loading screen and shows the form (the jobs INSERT
    // RLS policy is the real block).
    loadEmployerData().catch(() => {}).finally(() => { if (!cancelled) setUsageLoaded(true); });
    return () => { cancelled = true; };
  }, [navigate]); // eslint-disable-line

  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; }
      body { background-color: ${NAVY}; font-family: ${FONT_BODY}; color: ${TEXT}; margin: 0; }
      input, select, textarea { font-family: ${FONT_BODY}; }
      input::placeholder, textarea::placeholder { color: rgba(255,255,255,0.35); }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  const handleDescriptionFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setDescExtracting(true);
    setDescExtractError('');
    try {
      const text = await extractDocumentText(file);
      setJobDescription(text);
    } catch (err) {
      setDescExtractError(err.message);
    } finally {
      setDescExtracting(false);
    }
  };

  const handleSubmit = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) { navigate('/login'); return; }

    const employerId = await resolveEmployerId(supabase, userId);
    if (!employerId) { navigate('/login'); return; }
    const { data: employer } = await supabase.from('employers').select('company_name').eq('id', employerId).single();

    // featured is deliberately never set on the initial insert: the cap-enforcing
    // trigger only runs BEFORE UPDATE, so a newly-created job is always inserted
    // unfeatured, then flipped on via a follow-up update (below) that the trigger
    // actually sees. Setting featured:true directly on insert would silently
    // bypass the 4-slot cap.
    const { data: newJob, error } = await supabase.from('jobs').insert({
      employer_id: employerId,
      company_name: employer?.company_name,
      job_title: jobTitle,
      trade_category: tradeCategory,
      job_type: jobType,
      union_status: unionStatus,
      salary_min: salaryMin,
      salary_max: salaryMax,
      min_years_experience: minYearsExperience,
      certifications_required: certTags.length > 0 ? certTags : null,
      certifications_preferred: certPreferredTags.length > 0 ? certPreferredTags : null,
      licenses_required: tradeCategory === 'CDL/Truck Driver' && requireCdl ? ['CDL'] : null,
      job_description: jobDescription,
      city,
      state,
      zip: zip || null,
      shift: shift || null,
      per_diem: perDiem,
      per_diem_amount: perDiem ? perDiemAmount : null,
      sign_on_bonus_amount: signOnBonus ? (parseFloat(signOnBonusAmount) || null) : null,
      travel_required: travelRequired,
      travel_details: travelRequired ? travelDetails : null,
      benefits: benefits.length > 0 ? benefits : null,
      urgently_hiring: urgentlyHiring,
      // A posting always represents one job-slot regardless of how many people
      // will be hired from it -- number_of_openings just tells workers how many
      // identical roles are open. Defaults to 1 (single opening) at the DB level.
      number_of_openings: multipleOpenings ? Math.max(2, parseInt(numberOfOpenings, 10) || 2) : 1,
      willing_to_train: willingToTrain,
      physical_requirements: physicalRequirements.length > 0 ? physicalRequirements : null,
      expires_at: expiresAt || null,
    }).select('id').single();

    if (error) { alert(error.message); return; }

    if (featured && newJob?.id) {
      const { error: featError } = await supabase.from('jobs').update({ featured: true }).eq('id', newJob.id);
      if (featError) {
        alert(t('employer.postJob.featuredCapErrorMessage', { limit: planUsage?.featured_limit ?? 4 }));
      }
    }

    navigate('/employer/dashboard');
  };

  const jobLimitReached = planUsage && planUsage.job_count >= planUsage.job_limit;
  const isPlus = planTier === 'plus';
  const featuredCapReached = !!(planUsage && planUsage.featured_count >= planUsage.featured_limit && !featured);
  // Checked ahead of the job-limit block below: an employer with no active subscription at
  // all (never checked out, or checked out and later cancelled) is a more fundamental block
  // than "you've used up your tier's jobs" -- they need to subscribe first, not just wait for
  // a slot to free up. Real enforcement is the jobs INSERT RLS policy either way; this is UX.
  const subscriptionRequired = planUsage && !planUsage.has_active_subscription;

  if (!usageLoaded) {
    return (
      <div style={{ fontFamily: FONT_BODY, backgroundColor: NAVY, minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div role="status" data-postjob-loading style={{ color: AMBER, fontSize: '16px' }}>{t('common.loading')}</div>
      </div>
    );
  }

  if (subscriptionRequired) {
    return (
      <div style={{ fontFamily: FONT_BODY, backgroundColor: NAVY, minHeight: '100dvh' }}>
        <nav style={{
          position: 'sticky', top: 0, zIndex: 200, height: '60px',
          backgroundColor: NAVY,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 2rem',
        }}>
          <div onClick={() => navigate('/employers')} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <VocationWorksLogo variant="reversed" size={36} />
          </div>
          <LanguageToggle dark />
        </nav>
        <div style={{ maxWidth: '520px', margin: '0 auto', padding: '3rem 1.25rem', textAlign: 'center' }}>
          <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.2rem', color: TEXT, letterSpacing: '0.03em', marginBottom: '0.75rem' }}>
            {t('employer.postJob.subscriptionRequiredHeading')}
          </h1>
          <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            {t('employer.postJob.subscriptionRequiredBody', { days: trialDays })}
          </p>
          <span
            onClick={() => navigate('/employer/pricing')}
            style={{ display: 'inline-flex', alignItems: 'center', minHeight: '44px', backgroundColor: AMBER, color: NAVY, fontWeight: 700, fontSize: '0.9rem', padding: '12px 28px', borderRadius: '8px', cursor: 'pointer', boxSizing: 'border-box' }}
          >
            {t('employer.postJob.viewPricingButton')}
          </span>
        </div>
      </div>
    );
  }

  if (jobLimitReached) {
    return (
      <div style={{ fontFamily: FONT_BODY, backgroundColor: NAVY, minHeight: '100dvh' }}>
        <nav style={{
          position: 'sticky', top: 0, zIndex: 200, height: '60px',
          backgroundColor: NAVY,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 2rem',
        }}>
          <div onClick={() => navigate('/employers')} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
            <VocationWorksLogo variant="reversed" size={36} />
          </div>
          <LanguageToggle dark />
        </nav>
        <div style={{ maxWidth: '520px', margin: '0 auto', padding: '3rem 1.25rem', textAlign: 'center' }}>
          <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.2rem', color: TEXT, letterSpacing: '0.03em', marginBottom: '0.75rem' }}>
            {t('employer.postJob.jobLimitReachedHeading')}
          </h1>
          <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, marginBottom: '1.5rem' }}>
            {t('employer.postJob.jobLimitReachedBody', { count: planUsage.job_count, limit: planUsage.job_limit })}
          </p>
          <span
            onClick={() => navigate('/employer/pricing')}
            style={{ color: AMBER, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}
          >
            {t('employer.dashboard.team.viewPricingLink')} →
          </span>
          <div style={{ marginTop: '0.75rem' }}>
            <span
              onClick={() => navigate('/employer/dashboard')}
              style={{ color: 'rgba(255,255,255,0.55)', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
            >
              {t('employer.postJob.backToDashboard')}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: FONT_BODY, backgroundColor: NAVY, minHeight: '100dvh' }}>

      {/* Navbar */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 200, height: '60px',
        backgroundColor: NAVY,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 2rem',
      }}>
        <div onClick={() => navigate('/employers')} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
          <VocationWorksLogo variant="reversed" size={36} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
          <LanguageToggle dark />
          <span
            onClick={() => navigate('/employer/dashboard')}
            style={{ fontSize: '0.875rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', cursor: 'pointer' }}
          >
            {t('employer.postJob.backToDashboard')}
          </span>
        </div>
      </nav>

      {/* Page title */}
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '2.5rem 1.25rem 0' }}>
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.8rem', color: TEXT, letterSpacing: '0.04em', marginBottom: '1.5rem' }}>
          {t('employer.postJob.pageTitlePost')}
        </h1>
      </div>

      {/* Form card */}
      <div style={{
        maxWidth: '700px', margin: '0 auto', padding: '0 1.25rem 3rem',
      }}>
        <div style={{
          backgroundColor: NAVY_MID, borderRadius: '16px',
          border: '1px solid rgba(255,255,255,0.07)',
          padding: '32px',
        }}>

          {/* Job title */}
          <div style={fld}>
            <label style={lbl}>{t('employer.postJob.jobTitleLabel')}</label>
            <input
              style={inp}
              placeholder={t('employer.postJob.jobTitlePlaceholder')}
              value={jobTitle}
              onChange={e => setJobTitle(e.target.value)}
            />
          </div>

          {/* Urgently hiring */}
          <div style={{ ...fld, padding: '12px 14px', borderRadius: '8px', backgroundColor: 'rgba(226,75,74,0.06)', border: '1px solid rgba(226,75,74,0.25)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700, color: '#F09595' }}>
              <input
                type="checkbox"
                checked={urgentlyHiring}
                onChange={e => setUrgentlyHiring(e.target.checked)}
                style={{ width: '15px', height: '15px', accentColor: '#E24B4A', cursor: 'pointer' }}
              />
              <LineIcon name="flame" size={16} color={YELLOW} />
              {t('employer.postJob.urgentlyHiringLabel')}
            </label>
            <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', margin: '6px 0 0 23px' }}>
              {t('employer.postJob.urgentlyHiringHint')}
            </p>
          </div>

          {/* Multiple openings */}
          <div style={fld}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, color: TEXT }}>
              <input
                type="checkbox"
                checked={multipleOpenings}
                onChange={e => { setMultipleOpenings(e.target.checked); if (!e.target.checked) setNumberOfOpenings(''); }}
                style={{ width: '15px', height: '15px', accentColor: AMBER, cursor: 'pointer' }}
              />
              {t('employer.postJob.multipleOpeningsLabel')}
            </label>
            {multipleOpenings && (
              <input
                type="number"
                min="2"
                style={{ ...inp, marginTop: '8px' }}
                placeholder={t('employer.postJob.multipleOpeningsPlaceholder')}
                value={numberOfOpenings}
                onChange={e => setNumberOfOpenings(e.target.value)}
              />
            )}
          </div>

          {/* Willing to train */}
          <div style={fld}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, color: TEXT }}>
              <input
                type="checkbox"
                checked={willingToTrain}
                onChange={e => setWillingToTrain(e.target.checked)}
                style={{ width: '15px', height: '15px', accentColor: AMBER, cursor: 'pointer' }}
              />
              {t('employer.postJob.willingToTrainLabel')}
            </label>
          </div>

          {/* Featured -- Plus tier only, fully invisible for Basic/Standard (confirmed decision) */}
          {isPlus && (
            <div style={{ ...fld, padding: '12px 14px', borderRadius: '8px', backgroundColor: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,0.35)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: featuredCapReached ? 'default' : 'pointer', fontSize: '0.88rem', fontWeight: 700, color: TEXT }}>
                <input
                  type="checkbox"
                  checked={featured}
                  disabled={featuredCapReached}
                  onChange={e => setFeatured(e.target.checked)}
                  style={{ width: '15px', height: '15px', accentColor: AMBER, cursor: featuredCapReached ? 'default' : 'pointer' }}
                />
                <LineIcon name="star" size={16} color={AMBER} filled={featured} />
                {t('employer.postJob.featuredLabel')}
              </label>
              {planUsage && (
                <p style={{ fontSize: '0.75rem', color: featuredCapReached ? '#F09595' : 'rgba(255,255,255,0.55)', margin: '6px 0 0 23px' }}>
                  {featuredCapReached
                    ? t('employer.postJob.featuredCapReachedHint', { limit: planUsage.featured_limit })
                    : t('employer.postJob.featuredSlotsUsed', { count: planUsage.featured_count, limit: planUsage.featured_limit })}
                </p>
              )}
            </div>
          )}

          {/* Trade category */}
          <div style={fld}>
            <label style={lbl}>{t('employer.postJob.tradeFilterLabel')}</label>
            <select style={sel} value={tradeCategory} onChange={e => setTradeCategory(e.target.value)}>
              <option value="">{t('employer.postJob.tradeFilterPlaceholder')}</option>
              {TRADE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{t(`worker.tradeNames.${opt.key}`)}</option>)}
            </select>
            <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)', marginTop: '6px' }}>
              {t('employer.postJob.tradeFilterHint')}
            </p>
          </div>

          {/* CDL requirement -- only relevant for the CDL/Truck Driver trade */}
          {tradeCategory === 'CDL/Truck Driver' && (
            <div style={fld}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, color: TEXT }}>
                <input
                  type="checkbox"
                  checked={requireCdl}
                  onChange={e => setRequireCdl(e.target.checked)}
                  style={{ width: '15px', height: '15px', accentColor: AMBER, cursor: 'pointer' }}
                />
                {t('employer.postJob.requireCdlLabel')}
              </label>
            </div>
          )}

          {/* Job type */}
          <div style={fld}>
            <label style={{ ...lbl, marginBottom: '0.65rem' }}>{t('employer.postJob.jobTypeLabel')}</label>
            <ToggleGroup
              options={JOB_TYPE_OPTIONS}
              value={jobType}
              onChange={setJobType}
            />
          </div>

          {/* Union status */}
          <div style={fld}>
            <label style={{ ...lbl, marginBottom: '0.65rem' }}>{t('employer.postJob.unionStatusLabel')}</label>
            <ToggleGroup
              options={UNION_STATUS_OPTIONS}
              value={unionStatus}
              onChange={setUnionStatus}
            />
          </div>

          {/* Salary range */}
          <div style={{ marginBottom: '0.4rem' }}>
            <label style={lbl}>{t('employer.postJob.salaryRangeLabel')}</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <input
                style={inp}
                placeholder={t('employer.postJob.salaryMinPlaceholder')}
                value={salaryMin}
                onChange={e => setSalaryMin(e.target.value)}
              />
              <input
                style={inp}
                placeholder={t('employer.postJob.salaryMaxPlaceholder')}
                value={salaryMax}
                onChange={e => setSalaryMax(e.target.value)}
              />
            </div>
          </div>
          <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)', marginBottom: '1.25rem', marginTop: '6px' }}>
            {t('employer.postJob.salaryHint')}
          </p>

          {/* Minimum years experience */}
          <div style={fld}>
            <label style={lbl}>{t('employer.postJob.minExperienceLabel')}</label>
            <select style={sel} value={minYearsExperience} onChange={e => setMinYearsExperience(e.target.value)}>
              <option value="">{t('employer.postJob.minExperiencePlaceholder')}</option>
              <option value="Less than 1 year">{t('employer.postJob.experienceOptions.lessThan1')}</option>
              <option value="1 to 3 years">{t('employer.postJob.experienceOptions.oneToThree')}</option>
              <option value="4 to 6 years">{t('employer.postJob.experienceOptions.fourToSix')}</option>
              <option value="7 to 10 years">{t('employer.postJob.experienceOptions.sevenToTen')}</option>
              <option value="10+ years">{t('employer.postJob.experienceOptions.tenPlus')}</option>
            </select>
          </div>

          {/* Required certifications */}
          <div style={fld}>
            <label style={lbl}>{t('employer.postJob.requiredCertsLabel')}</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <div style={{
                flex: 1, border: `1.5px solid ${BORDER}`, borderRadius: '8px',
                backgroundColor: INPUT_BG, padding: '8px 10px',
                display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center',
                minHeight: '46px', cursor: 'text',
              }}
                onClick={() => document.getElementById('cert-tag-input')?.focus()}
              >
                {certTags.map((cert, i) => (
                  <span key={i} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    backgroundColor: AMBER, color: NAVY,
                    borderRadius: '5px', padding: '3px 8px 3px 10px',
                    fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.4,
                  }}>
                    {cert}
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); setCertTags(certTags.filter((_, j) => j !== i)); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: NAVY, fontSize: '14px', lineHeight: 1, padding: '0 0 0 2px', fontWeight: 700, display: 'flex', alignItems: 'center' }}
                    >×</button>
                  </span>
                ))}
                <input
                  id="cert-tag-input"
                  type="text"
                  value={certInput}
                  onChange={e => setCertInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const tag = certInput.trim();
                      if (tag && !certTags.includes(tag)) setCertTags([...certTags, tag]);
                      setCertInput('');
                    }
                  }}
                  placeholder={certTags.length === 0 ? t('employer.postJob.certTagPlaceholder') : ''}
                  style={{
                    flex: 1, border: 'none', outline: 'none', fontSize: '16px',
                    fontFamily: FONT_BODY, color: TEXT, backgroundColor: 'transparent',
                    minWidth: '180px', padding: '3px 4px',
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const tag = certInput.trim();
                  if (tag && !certTags.includes(tag)) setCertTags([...certTags, tag]);
                  setCertInput('');
                }}
                style={{
                  background: AMBER, border: 'none', color: NAVY,
                  fontSize: '0.88rem', fontWeight: 700, padding: '11px 16px',
                  borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY,
                  flexShrink: 0, alignSelf: 'flex-start',
                }}
              >{t('employer.postJob.addTag')}</button>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', margin: '6px 0 0' }}>
              {t('employer.postJob.requiredCertsHint')}
            </p>
          </div>

          {/* Preferred certifications */}
          <div style={fld}>
            <label style={lbl}>{t('employer.postJob.preferredCertsLabel')}</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <div style={{
                flex: 1, border: `1.5px solid ${BORDER}`, borderRadius: '8px',
                backgroundColor: INPUT_BG, padding: '8px 10px',
                display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center',
                minHeight: '46px', cursor: 'text',
              }}
                onClick={() => document.getElementById('cert-preferred-tag-input')?.focus()}
              >
                {certPreferredTags.map((cert, i) => (
                  <span key={i} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    backgroundColor: 'rgba(255,255,255,0.08)', color: TEXT,
                    borderRadius: '5px', padding: '3px 8px 3px 10px',
                    fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.4,
                  }}>
                    {cert}
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); setCertPreferredTags(certPreferredTags.filter((_, j) => j !== i)); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: TEXT, fontSize: '14px', lineHeight: 1, padding: '0 0 0 2px', fontWeight: 700, display: 'flex', alignItems: 'center' }}
                    >×</button>
                  </span>
                ))}
                <input
                  id="cert-preferred-tag-input"
                  type="text"
                  value={certPreferredInput}
                  onChange={e => setCertPreferredInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const tag = certPreferredInput.trim();
                      if (tag && !certPreferredTags.includes(tag)) setCertPreferredTags([...certPreferredTags, tag]);
                      setCertPreferredInput('');
                    }
                  }}
                  placeholder={certPreferredTags.length === 0 ? t('employer.postJob.certTagPlaceholder') : ''}
                  style={{
                    flex: 1, border: 'none', outline: 'none', fontSize: '16px',
                    fontFamily: FONT_BODY, color: TEXT, backgroundColor: 'transparent',
                    minWidth: '180px', padding: '3px 4px',
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  const tag = certPreferredInput.trim();
                  if (tag && !certPreferredTags.includes(tag)) setCertPreferredTags([...certPreferredTags, tag]);
                  setCertPreferredInput('');
                }}
                style={{
                  background: AMBER, border: 'none', color: NAVY,
                  fontSize: '0.88rem', fontWeight: 700, padding: '11px 16px',
                  borderRadius: '8px', cursor: 'pointer', fontFamily: FONT_BODY,
                  flexShrink: 0, alignSelf: 'flex-start',
                }}
              >{t('employer.postJob.addTag')}</button>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', margin: '6px 0 0' }}>
              {t('employer.postJob.preferredCertsHint')}
            </p>
          </div>

          {/* Shift */}
          <div style={fld}>
            <label style={lbl}>{t('employer.postJob.shiftLabel')}</label>
            <select style={sel} value={shift} onChange={e => setShift(e.target.value)}>
              <option value="">{t('employer.postJob.shiftPlaceholder')}</option>
              {SHIFT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{t(`employer.postJob.shiftOptions.${opt.key}`)}</option>)}
            </select>
          </div>

          {/* Per diem */}
          <div style={fld}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, color: TEXT }}>
              <input
                type="checkbox"
                checked={perDiem}
                onChange={e => { setPerDiem(e.target.checked); if (!e.target.checked) setPerDiemAmount(''); }}
                style={{ width: '15px', height: '15px', accentColor: AMBER, cursor: 'pointer' }}
              />
              {t('employer.postJob.perDiemLabel')}
            </label>
            {perDiem && (
              <input
                style={{ ...inp, marginTop: '8px' }}
                placeholder={t('employer.postJob.perDiemPlaceholder')}
                value={perDiemAmount}
                onChange={e => setPerDiemAmount(e.target.value)}
              />
            )}
          </div>

          {/* Sign-on bonus */}
          <div style={fld}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, color: TEXT }}>
              <input
                type="checkbox"
                checked={signOnBonus}
                onChange={e => { setSignOnBonus(e.target.checked); if (!e.target.checked) setSignOnBonusAmount(''); }}
                style={{ width: '15px', height: '15px', accentColor: AMBER, cursor: 'pointer' }}
              />
              {t('employer.postJob.signOnBonusLabel')}
            </label>
            {signOnBonus && (
              <input
                type="number"
                min="0"
                style={{ ...inp, marginTop: '8px' }}
                placeholder={t('employer.postJob.signOnBonusPlaceholder')}
                value={signOnBonusAmount}
                onChange={e => setSignOnBonusAmount(e.target.value)}
              />
            )}
          </div>

          {/* Travel required */}
          <div style={fld}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 600, color: TEXT }}>
              <input
                type="checkbox"
                checked={travelRequired}
                onChange={e => { setTravelRequired(e.target.checked); if (!e.target.checked) setTravelDetails(''); }}
                style={{ width: '15px', height: '15px', accentColor: AMBER, cursor: 'pointer' }}
              />
              {t('employer.postJob.travelRequiredLabel')}
            </label>
            {travelRequired && (
              <input
                style={{ ...inp, marginTop: '8px' }}
                placeholder={t('employer.postJob.travelRequiredPlaceholder')}
                value={travelDetails}
                onChange={e => setTravelDetails(e.target.value)}
              />
            )}
          </div>

          {/* Benefits */}
          <div style={fld}>
            <label style={lbl}>{t('employer.postJob.benefitsLabel')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '6px' }}>
              {BENEFIT_OPTIONS.map(b => (
                <label key={b} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.84rem', color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                  <input
                    type="checkbox"
                    checked={benefits.includes(b)}
                    onChange={() => setBenefits(benefits.includes(b) ? benefits.filter(x => x !== b) : [...benefits, b])}
                    style={{ width: '14px', height: '14px', accentColor: AMBER, cursor: 'pointer' }}
                  />
                  {t(`employer.benefitNames.${BENEFIT_KEY_MAP[b]}`)}
                </label>
              ))}
            </div>
          </div>

          {/* Physical requirements */}
          <div style={fld}>
            <label style={lbl}>{t('employer.postJob.physicalRequirementsLabel')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '6px' }}>
              {PHYSICAL_REQUIREMENT_OPTIONS.map(r => (
                <label key={r} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.84rem', color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                  <input
                    type="checkbox"
                    checked={physicalRequirements.includes(r)}
                    onChange={() => setPhysicalRequirements(physicalRequirements.includes(r) ? physicalRequirements.filter(x => x !== r) : [...physicalRequirements, r])}
                    style={{ width: '14px', height: '14px', accentColor: AMBER, cursor: 'pointer' }}
                  />
                  {t(`employer.physicalRequirementNames.${PHYSICAL_REQUIREMENT_KEY_MAP[r]}`)}
                </label>
              ))}
            </div>
          </div>

          {/* Job description */}
          <div style={fld}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '5px' }}>
              <label style={{ ...lbl, marginBottom: 0 }}>{t('employer.postJob.jobDescriptionLabel')}</label>
              <button
                type="button"
                onClick={() => descFileInputRef.current?.click()}
                disabled={descExtracting}
                style={{
                  background: 'none', border: `1px solid ${AMBER}`, borderRadius: '6px',
                  color: AMBER, fontSize: '0.78rem', fontWeight: 600, padding: '5px 10px',
                  cursor: descExtracting ? 'default' : 'pointer', opacity: descExtracting ? 0.6 : 1, flexShrink: 0,
                }}
              >
                {descExtracting ? t('employer.postJob.readingFile') : t('employer.postJob.uploadJobDescription')}
              </button>
              <input
                ref={descFileInputRef}
                type="file"
                accept=".docx,.pdf"
                style={{ display: 'none' }}
                onChange={handleDescriptionFile}
              />
            </div>
            <textarea
              style={{ ...inp, minHeight: '150px', resize: 'vertical', lineHeight: 1.6 }}
              placeholder={t('employer.postJob.jobDescriptionPlaceholder')}
              value={jobDescription}
              onChange={e => setJobDescription(e.target.value)}
            />
            {descExtractError && (
              <p style={{ fontSize: '0.78rem', color: '#F87171', fontWeight: 600, margin: '6px 0 0' }}>
                {descExtractError}
              </p>
            )}
            <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', margin: '6px 0 0' }}>
              {t('employer.postJob.jobDescriptionHint')}
            </p>
          </div>

          {/* City + State */}
          <div style={g2}>
            <div>
              <label style={lbl}>{t('employer.postJob.cityLabel')}</label>
              <input style={inp} placeholder={t('employer.postJob.cityPlaceholder')} value={city} onChange={e => setCity(e.target.value)} />
            </div>
            <div>
              <label style={lbl}>{t('employer.postJob.stateLabel')}</label>
              <select style={sel} value={state} onChange={e => setState(e.target.value)}>
                <option value="IL">Illinois</option>
                <option value="IN">Indiana</option>
                <option value="WI">Wisconsin</option>
                <option value="MI">Michigan</option>
                <option value="OH">Ohio</option>
                <option value="MO">Missouri</option>
                <option value="IA">Iowa</option>
                <option value="MN">Minnesota</option>
              </select>
            </div>
          </div>

          {/* ZIP code -- optional; lets nearby workers find this job via commute-radius filtering */}
          <div style={fld}>
            <label style={lbl}>{t('employer.postJob.zipLabel')}</label>
            <input style={inp} placeholder={t('employer.postJob.zipPlaceholder')} value={zip} onChange={e => setZip(e.target.value)} />
            <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', marginTop: '6px' }}>
              {t('employer.postJob.zipHint')}
            </p>
          </div>

          {/* Posting expiration date */}
          <div style={fld}>
            <label style={lbl}>{t('employer.postJob.expiresAtLabel')}</label>
            <input
              type="date"
              style={inp}
              value={expiresAt}
              onChange={e => setExpiresAt(e.target.value)}
            />
            <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.55)', margin: '6px 0 0' }}>
              {t('employer.postJob.expiresAtHint')}
            </p>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button
              type="button"
              onClick={() => navigate('/employer/dashboard')}
              style={{
                backgroundColor: 'rgba(255,255,255,0.08)', color: TEXT,
                border: 'none', borderRadius: '8px',
                padding: '13px 24px', fontWeight: 600, fontSize: '0.95rem',
                cursor: 'pointer', fontFamily: FONT_BODY,
              }}
            >
              {t('employer.postJob.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              style={{
                backgroundColor: AMBER, color: NAVY,
                border: 'none', borderRadius: '8px',
                padding: '13px 28px', fontWeight: 700, fontSize: '0.95rem',
                cursor: 'pointer', fontFamily: FONT_BODY,
              }}
            >
              {t('employer.postJob.submitPost')}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
